import { Component, ChangeDetectionStrategy, DOCUMENT, Inject, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { RootState, fromProgram } from "./store";
import { NavigationEnd, Router } from "@angular/router";
import { delay, filter, first, Observable, of, Subject, takeUntil } from "rxjs";

// The page's own skeletons wait 150ms and fade in over 200ms (theme/skeleton.scss): the pre-boot one stays over them
// until then, so one skeleton hands over to the other without a blank frame, and then fades out itself
const PREBOOT_HANDOVER_MS = 350;
const PREBOOT_FADE_MS = 200;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly loadingFullScreen$: Observable<boolean> = this.store.select(fromProgram.selectLoadingFullScreen);

  private readonly $unsubscribeAll = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public ngOnInit(): void {
    // Screen readers pick the pronunciation from <html lang> (WCAG 3.1.1)
    this.translateService.onLangChange
      .pipe(takeUntil(this.$unsubscribeAll))
      .subscribe(({ lang }) => (this.document.documentElement.lang = lang));
    const currentLang = this.translateService.getCurrentLang();
    if (currentLang) this.document.documentElement.lang = currentLang;

    // index.html paints a skeleton of the page before the app boots; it leaves once the first page is drawn. A known shop
    // navigates from the app initializer, so that page may already be drawn before this component starts
    const firstPageDrawn$ = this.router.navigated
      ? of(null)
      : this.router.events.pipe(filter(event => event instanceof NavigationEnd), first());
    firstPageDrawn$
      .pipe(
        delay(PREBOOT_HANDOVER_MS),
        takeUntil(this.$unsubscribeAll),
      )
      .subscribe(() => this.removePreboot());
  }

  private removePreboot(): void {
    const preboot = this.document.getElementById("app-preboot");
    if (!preboot) return;
    preboot.classList.add("--leaving");
    setTimeout(() => preboot.remove(), PREBOOT_FADE_MS);
  }

  public ngOnDestroy(): void {
    this.$unsubscribeAll.next();
    this.$unsubscribeAll.complete();
  }
}
